import type { ComponentPropsWithRef } from 'react';
import styled from 'styled-components';
import { ifProp, prop, theme, withProp } from 'styled-tools';

import { Icon, getIconA11y } from '@ui/icons';

export type BadgeIconProps = Omit<ComponentPropsWithRef<'span'>, 'children'> & {
  /** Bundled registry name; unknown names render the Icon error glyph. */
  name: string;
  /** Badge diameter in pixels; defaults to theme.iconSizes.md. */
  size?: number;
  backgroundColor?: string;
  /** Optional outline; omitted or transparent renders a solid badge. */
  borderColor?: string;
  /** Outline width in pixels; defaults to theme.borderWidths.sm. Requires borderColor. */
  borderWidth?: number;
  fillColor?: string;
  degreesOfIconRotation?: number;
  flipOnXAxis?: boolean;
};

const StyledBadge = styled.span<{
  $size?: number;
  $backgroundColor?: string;
  $borderColor?: string;
  $borderWidth?: number;
  $hasOutline: boolean;
  $fillColor?: string;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  vertical-align: middle;
  box-sizing: border-box;
  width: ${withProp(['$size', 'theme.iconSizes.md'], (size, defaultSize) => `${size ?? defaultSize}px`)};
  height: ${withProp(['$size', 'theme.iconSizes.md'], (size, defaultSize) => `${size ?? defaultSize}px`)};
  border-radius: ${theme('radii.full')};
  border-style: ${ifProp('$hasOutline', 'solid', 'none')};
  border-width: ${withProp(['$borderWidth', 'theme.borderWidths.sm'], (width, defaultWidth) =>
    width === undefined ? defaultWidth : `${width}px`,
  )};
  border-color: ${prop('$borderColor', 'transparent')};
  background-color: ${prop('$backgroundColor', theme('colors.primary'))};
  background-clip: padding-box;
  color: ${prop('$fillColor', theme('colors.miscBadgeIconColor'))};
`;

/** A circular, offline-safe icon badge. Label it when it conveys information without adjacent text. */
export default function BadgeIcon({
  name,
  size,
  backgroundColor,
  borderColor,
  borderWidth,
  fillColor,
  degreesOfIconRotation,
  flipOnXAxis,
  title,
  role,
  'aria-label': ariaLabel,
  ...rest
}: BadgeIconProps) {
  const a11yProps = getIconA11y({
    title,
    ariaLabel,
    role: role ?? (rest['aria-labelledby'] ? 'img' : undefined),
  });

  return (
    <StyledBadge
      $size={size}
      $backgroundColor={backgroundColor}
      $borderColor={borderColor}
      $borderWidth={borderWidth}
      $hasOutline={Boolean(borderColor && borderColor !== 'transparent')}
      $fillColor={fillColor}
      {...a11yProps}
      {...rest}
    >
      {/* Keep the badge foreground when Icon renders its standalone red error fallback. */}
      <Icon
        name={name}
        width="100%"
        height="100%"
        fillColor={fillColor}
        degreesOfIconRotation={degreesOfIconRotation}
        flipOnXAxis={flipOnXAxis}
        style={{ color: 'inherit' }}
        aria-hidden="true"
      />
    </StyledBadge>
  );
}
